/**
 * Service Display Settings
 *
 * Centralized configuration for how services are displayed across all apps.
 * Stored in Firebase for persistence, with local caching.
 */

import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../config/firebase';

export interface ServiceDisplaySettings {
  // Display Options
  showPrices: boolean;
  showDurations: boolean;
  showServiceImages: boolean;
  showCategoryIcons: boolean;

  // Behavior Options
  defaultExpanded: boolean;        // Categories expanded by default
  allowMultiSelect: boolean;       // Allow selecting multiple services
  showSelectAllPerCategory: boolean; // Show "Select All" button per category
  showSearchFilter: boolean;       // Show search/filter input

  // Style Options
  selectionStyle: 'checkbox' | 'button' | 'card';

  // Category Order (array of category IDs in display order)
  categoryOrder: string[];

  // Timestamps
  updatedAt: string;
}

const DEFAULT_SETTINGS: ServiceDisplaySettings = {
  showPrices: true,
  showDurations: true,
  showServiceImages: true,
  showCategoryIcons: true,
  defaultExpanded: true,           // User preference: default expanded
  allowMultiSelect: true,
  showSelectAllPerCategory: true,
  showSearchFilter: true,
  selectionStyle: 'checkbox',
  categoryOrder: [],               // Empty = use sortOrder from categories
  updatedAt: new Date().toISOString(),
};

class ServiceDisplaySettingsService {
  private settingsDocRef = doc(db, 'settings', 'serviceDisplay');
  private cachedSettings: ServiceDisplaySettings | null = null;
  private listeners: ((settings: ServiceDisplaySettings) => void)[] = [];
  private unsubscribe: (() => void) | null = null;

  constructor() {
    this.initializeSubscription();
    // RESILIENCE (Jose 2026-10-29 audit): re-subscribe on auth-ready so a pre-auth denied read self-heals.
    try { onAuthStateChanged(auth, (u) => { if (u) this.initializeSubscription(); }); } catch { /* ignore */ }
  }

  private initializeSubscription() {
    try { this.unsubscribe?.(); } catch { /* ignore */ }
    this.unsubscribe = onSnapshot(this.settingsDocRef, (docSnap) => {
      if (docSnap.exists()) {
        this.cachedSettings = { ...DEFAULT_SETTINGS, ...docSnap.data() } as ServiceDisplaySettings;
      } else {
        this.cachedSettings = DEFAULT_SETTINGS;
      }
      // Notify all listeners
      this.listeners.forEach(listener => listener(this.cachedSettings!));
    }, (error) => {
      console.error('Error listening to service display settings:', error);
      this.cachedSettings = DEFAULT_SETTINGS;
    });
  }

  /**
   * Get current settings (returns cached if available, fetches otherwise)
   */
  async getSettings(): Promise<ServiceDisplaySettings> {
    if (this.cachedSettings) {
      return this.cachedSettings;
    }

    try {
      const docSnap = await getDoc(this.settingsDocRef);
      if (docSnap.exists()) {
        this.cachedSettings = { ...DEFAULT_SETTINGS, ...docSnap.data() } as ServiceDisplaySettings;
      } else {
        // Initialize with defaults
        await this.saveSettings(DEFAULT_SETTINGS);
        this.cachedSettings = DEFAULT_SETTINGS;
      }
      return this.cachedSettings;
    } catch (error) {
      console.error('Error fetching service display settings:', error);
      return DEFAULT_SETTINGS;
    }
  }

  /**
   * Get settings synchronously (returns cached or defaults)
   */
  getSettingsSync(): ServiceDisplaySettings {
    return this.cachedSettings || DEFAULT_SETTINGS;
  }

  /**
   * Save settings to Firebase
   */
  async saveSettings(settings: Partial<ServiceDisplaySettings>): Promise<boolean> {
    try {
      const updatedSettings = {
        ...this.cachedSettings,
        ...settings,
        updatedAt: new Date().toISOString(),
      };

      await setDoc(this.settingsDocRef, updatedSettings);
      this.cachedSettings = updatedSettings as ServiceDisplaySettings;
      return true;
    } catch (error) {
      console.error('Error saving service display settings:', error);
      return false;
    }
  }

  /**
   * Update a single setting
   */
  async updateSetting<K extends keyof ServiceDisplaySettings>(
    key: K,
    value: ServiceDisplaySettings[K]
  ): Promise<boolean> {
    return this.saveSettings({ [key]: value });
  }

  /**
   * Update category order
   */
  async updateCategoryOrder(categoryIds: string[]): Promise<boolean> {
    return this.saveSettings({ categoryOrder: categoryIds });
  }

  /**
   * Subscribe to settings changes
   */
  subscribe(callback: (settings: ServiceDisplaySettings) => void): () => void {
    this.listeners.push(callback);

    // Immediately call with current settings
    if (this.cachedSettings) {
      callback(this.cachedSettings);
    }

    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  /**
   * Reset to default settings
   */
  async resetToDefaults(): Promise<boolean> {
    return this.saveSettings(DEFAULT_SETTINGS);
  }
}

export const serviceDisplaySettings = new ServiceDisplaySettingsService();
export default serviceDisplaySettings;
